import { queryOptions } from "@tanstack/react-query";
import z from "zod/v4";
import { env } from "@/env";
import { parseApiErrorFromResponse } from "@/lib/api-errors";
import { fxQueryKeys } from "@/lib/fx-query-keys";
import { createVolumeHeaders } from "@/lib/volume-api-utils";

const notificationType = z.enum(["trade_completed", "payout_completed"]);
const notificationChannel = z.enum(["email", "whatsapp"]);

const fxNotificationConfigSchema = z.object({
  type: notificationType,
  configId: z.string().nullable(),
  updatedByUserId: z.string().nullable(),
  updatedAt: z.number().nullable(),
  recipients: z.array(
    z.object({
      id: z.string(),
      userId: z.string(),
      email: z.string(),
      phoneNumber: z.string().nullable(),
      channel: notificationChannel,
      createdAt: z.number(),
    }),
  ),
});

const fxNotificationConfigResponseSchema = z.object({
  notifications: z.array(fxNotificationConfigSchema),
});

export type FxNotificationConfig = z.infer<typeof fxNotificationConfigSchema>;
export type FxNotificationConfigResponse = z.infer<
  typeof fxNotificationConfigResponseSchema
>;

const fxRateAlertConfigSchema = z.object({
  id: z.string(),
  cadence: z.enum(["hourly", "daily"]),
  dailyTime: z.string().nullable(),
  timezone: z.string(),
  lastSentAt: z.number().nullable(),
  updatedByUserId: z.string().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
  pairs: z.array(
    z.object({
      id: z.string(),
      sourceCurrency: z.string(),
      destinationCurrency: z.string(),
    }),
  ),
  recipients: z.array(
    z.object({
      id: z.string(),
      recipientType: z.enum(["user", "group"]),
      userId: z.string().nullable(),
      email: z.string().nullable(),
      phoneNumber: z.string().nullable(),
      channel: notificationChannel,
      groupId: z.string().nullable(),
      displayName: z.string(),
    }),
  ),
});

const fxRateAlertConfigResponseSchema = z.object({
  config: fxRateAlertConfigSchema.nullable(),
});

export type FxRateAlertConfig = z.infer<typeof fxRateAlertConfigSchema>;
export type FxRateAlertConfigResponse = z.infer<
  typeof fxRateAlertConfigResponseSchema
>;

export interface ReplaceFxNotificationConfigRequest {
  readonly notifications: readonly {
    readonly type: z.infer<typeof notificationType>;
    readonly destinations: readonly {
      readonly userId: string;
      readonly channel: z.infer<typeof notificationChannel>;
    }[];
  }[];
}

export interface ReplaceFxRateAlertConfigRequest {
  readonly cadence: "hourly" | "daily";
  readonly dailyTime: string | null;
  readonly timezone: string;
  readonly pairs: readonly {
    readonly sourceCurrency: string;
    readonly destinationCurrency: string;
  }[];
  readonly recipients: readonly (
    | {
        readonly recipientType: "user";
        readonly userId: string;
        readonly channel: "email" | "whatsapp";
        readonly displayName: string;
      }
    | {
        readonly recipientType: "group";
        readonly groupId: string;
        readonly displayName: string;
      }
  )[];
}

interface RequestContext {
  readonly accessToken: string;
  readonly applicationId: string;
}

async function requestJson<T>(
  { accessToken, applicationId }: RequestContext,
  path: string,
  schema: z.ZodType<T>,
  fallbackMessage: string,
  init?: { readonly method: "PUT"; readonly body: unknown },
): Promise<T> {
  const response = await fetch(
    `${env.VITE_MERCHANT_PORTAL_BACKEND_URL}${path}`,
    {
      method: init?.method ?? "GET",
      headers: createVolumeHeaders({
        accessToken,
        applicationId,
        contentTypeHeader: "application/json",
      }),
      ...(init ? { body: JSON.stringify(init.body) } : {}),
    },
  );
  if (!response.ok) {
    const parsed = await parseApiErrorFromResponse(response);
    throw new Error(parsed.message || fallbackMessage);
  }
  return schema.parse(await response.json());
}

export const fxNotificationConfigQuery = (context: RequestContext) =>
  queryOptions({
    queryKey: fxQueryKeys.notificationConfig(context.applicationId),
    queryFn: () =>
      requestJson(
        context,
        "/api/notifications/config",
        fxNotificationConfigResponseSchema,
        "Failed to load FX notification settings",
      ),
  });

export const fxRateAlertConfigQuery = (context: RequestContext) =>
  queryOptions({
    queryKey: fxQueryKeys.rateAlertConfig(context.applicationId),
    queryFn: () =>
      requestJson(
        context,
        "/api/rate-alerts/config",
        fxRateAlertConfigResponseSchema,
        "Failed to load FX rate alert settings",
      ),
  });

export const replaceFxNotificationConfig = (
  context: RequestContext,
  body: ReplaceFxNotificationConfigRequest,
) =>
  requestJson(
    context,
    "/api/notifications/config",
    fxNotificationConfigResponseSchema,
    "Failed to save FX notification settings",
    { method: "PUT", body },
  );

export const replaceFxRateAlertConfig = (
  context: RequestContext,
  body: ReplaceFxRateAlertConfigRequest,
) =>
  requestJson(
    context,
    "/api/rate-alerts/config",
    fxRateAlertConfigResponseSchema,
    "Failed to save FX rate alert settings",
    { method: "PUT", body },
  );
